<template>
    <MainContent 
        title='Crear nueva liquidación'
    >
        <template v-slot:header>
            <div v-if="! terminado">
                <CrearDireccionFiscalModal
                    @creado="getDireccionesFiscales()"
                />
                <div class="pt-2">
                    <b>*Las liquidaciones solo se podrán solicitar una vez que haya pasado el evento</b> 
                </div>
            </div>
            <RecordatorioTituloComponent v-else />
        </template>
        <template v-slot:content>
            <ContentOrLoading>
                <ValidationObserver v-if="! terminado" tag="form" ref="observerLiquidacion" @submit.prevent="submitFirstStep()">
                    <div class="row" v-if="!loading">
                        <div class="col-12 col-lg-6">
                            <div class="form-group">
                                <label for="fechas">Eventos disponibles:</label>
                                <ValidationProvider name="eventos" rules="required" mode="passive" v-slot="{ errors }">
                                    <multiselect 
                                        v-model="data.evento" 
                                        :options="eventos_disponibles"
                                        placeholder="Seleccionar eventos" 
                                        label="name" 
                                        track-by="id"
                                    >
                                        <template slot="noOptions"><strong>No dispones de más eventos para solicitar liquidación.</strong></template>
                                    </multiselect>
                                    <span class="error">{{ errors[0] }}</span>
                                </ValidationProvider>
                            </div>
                        </div>
                        <div class="col-12 col-lg-6" v-if="data.evento && ! data.evento.nif_fiscal">
                            <div class="form-group">
                                <label for="datos_fiscales">Seleccionar datos fiscales:</label>
                                <ValidationProvider name="direcciones fiscales" rules="required" mode="passive" v-slot="{ errors }">
                                    <multiselect
                                        v-model="data.direccionFiscal"
                                        :show-labels="false"
                                        :options="direccionesFiscales"
                                        :allow-empty="false"
                                        label="nif"
                                        :class="(errors[0] ? 'is-invalid' : '')"
                                        placeholder="Seleccione unos datos fiscales"
                                    />
                                    <span class="error">{{ errors[0] }}</span>
                                </ValidationProvider>
                            </div>
                        </div>
                        <div class="col-12" v-if="data.evento && data.evento.nif_fiscal">
                            <div class="row">
                                <div class="col-12">
                                    <h4>Antes de continuar comprueba que todos los datos estén bien:</h4>
                                </div>
                                <div class="col-12">
                                    <ul>
                                        <li>
                                            <b>Nombre: </b>{{ data.evento.nombre_fiscal }}
                                        </li>
                                        <li>
                                            <b>Nif: </b>{{ data.evento.nif_fiscal }}
                                        </li>
                                        <li>
                                            <b>Dirección: </b>{{ data.evento.direccion_fiscal }}
                                        </li>
                                        <li>
                                            <b>Código postal: </b>{{ data.evento.cp_fiscal }}
                                        </li>
                                        <li>
                                            <b>Ciudad: </b>{{ data.evento.ciudad_fiscal }}
                                        </li>
                                        <li>
                                            <b>Nº de cuenta: </b>{{ data.evento.cuenta_fiscal }}
                                        </li>
                                    </ul>
                                </div>
                                <div class="col-12">
                                    <p>
                                        <b>Si estos datos no son correctos haz click en el siguiente botón para editarlos:</b>
                                        <ModalEditar 
                                            :evento="data.evento"
                                            @editado="(newData) => data.evento = newData"
                                        />
                                    </p>
                                </div>
                                <div class="col-12 text-center">
                                    <b-button block type="submit">
                                        <i class="fa fa-arrow-down mr-3"></i>Continuar<i class="fa fa-arrow-down ml-3"></i>
                                    </b-button>
                                </div>
                            </div>
                        </div>
                        <div class="col-12" v-if="data.direccionFiscal">
                            <div class="row">
                                <div class="col-12">
                                    <h4>Antes de continuar comprueba que todos los datos estén bien:</h4>
                                </div>
                                <div class="col-12">
                                    <ul>
                                        <li>
                                            <b>Nombre: </b>{{ data.direccionFiscal.nombre }}
                                        </li>
                                        <li>
                                            <b>Nif: </b>{{ data.direccionFiscal.nif }}
                                        </li>
                                        <li>
                                            <b>Dirección: </b>{{ data.direccionFiscal.direccion }}
                                        </li>
                                        <li>
                                            <b>Código postal: </b>{{ data.direccionFiscal.cp }}
                                        </li>
                                        <li>
                                            <b>Ciudad: </b>{{ data.direccionFiscal.ciudad }}
                                        </li>
                                        <li>
                                            <b>Nº de cuenta: </b>{{ data.direccionFiscal.n_cuenta }}
                                        </li>
                                    </ul>
                                </div>
                                <div class="col-12">
                                    <p>
                                        <b>Si estos datos no son correctos haz click en el siguiente botón para editarlos:</b>
                                        <EditarDireccionFiscalModal
                                            :direccionFiscal="data.direccionFiscal"
                                            @editado="(newData) => data.direccionFiscal = newData"
                                        />
                                    </p>
                                </div>
                                <div class="col-12 text-center">
                                    <b-button block type="submit">
                                        <i class="fa fa-arrow-down mr-3"></i>Continuar<i class="fa fa-arrow-down ml-3"></i>
                                    </b-button>
                                </div>
                            </div>
                        </div>
                        <div class="col-12 mt-3" v-if="resumen.length > 0">
                            <div class="row">
                                <div class="col-12">
                                    <h4>Resumen provisional:</h4>
                                </div>
                                <template v-for="(linea, index) in resumen">
                                    <div class="col-12 col-lg-6" :key="index">
                                        <div class="row">
                                            <div class="col-12 ml-3">
                                                <h5>{{ linea.descripcion }}</h5>
                                                <ul>
                                                    <li>Total: {{ linea.total }}</li>
                                                    <li>Importe total: {{ linea.importe | formatear_precio }}</li>
                                                </ul>
                                            </div>
                                        </div>
                                    </div>
                                </template> 
                                
                                <div class="col-12 mt-3">
                                    <h5>Total: {{ total | formatear_precio }}</h5>
                                </div>
                                <div class="col-12">
                                    <b-button 
                                        :disabled="creando"
                                        block 
                                        @click="crearLiquidacion()"
                                    >
                                        Crear liquidación
                                    </b-button>
                                </div>
                            </div>                            
                        </div>
                    </div>
                </ValidationObserver>
                <RecordatorioComponent
                    v-else
                    @continuar="continuar"
                />
            </ContentOrLoading>
        </template>
    </MainContent>
</template>

<script>
import MainContent from '@/components/backend/components/MainContent';
import ContentOrLoading from '@/components/backend/components/ContentOrLoading';
import CrearDireccionFiscalModal from '@/components/backend/direcciones-fiscales/modals/CrearDireccionFiscalModal';
import EditarDireccionFiscalModal from '@/components/backend/direcciones-fiscales/modals/EditarDireccionFiscalModal';
import ModalEditar from '@/components/backend/eventos/compania/ModalEditar.vue';
import RecordatorioComponent from '@/components/backend/liquidaciones/common/RecordatorioComponent.vue';
import RecordatorioTituloComponent from '@/components/backend/liquidaciones/common/RecordatorioTituloComponent.vue';

export default {
    data() {
        return {
            data: {},
            eventos_disponibles: [],
            loading: true,
            direccionesFiscales: [],
            resumen: [],
            terminado: false,
            creando: false
        }
    },
    created() {
        this.getEventosDisponibles();
        this.getDireccionesFiscales();
    },
    computed: {
        total() {
            let total = 0;
            this.resumen.forEach(linea => {
                total += parseFloat(linea.importe);
            });

            return total;
        }
    },
    methods: {
        getEventosDisponibles() {
            const params = {
                filters : {}
            };

            this.$store.dispatch(this.config.getVuexAlias('back.liquidaciones.eventos.disponibles'), params).then(res => {
                this.eventos_disponibles = res.data.data;
            }).finally(() => {
                this.loading = false;
            });
        },
        getDireccionesFiscales() {
            const params = {
                filters : {}
            };

            this.$store.dispatch(this.config.getVuexAlias('back.teatro.direcciones.fiscales.index'), params).then(res => {
                this.direccionesFiscales = res.data.data;
            }).finally(() => {
                this.loading = false;
            });                
        },
        async submitFirstStep() {
            const isValid = await this.$refs.observerLiquidacion.validate();
            if(isValid) {
                this.getResumenLiquidacion();
            } else {
                this.$nextTick(function() {
                    this.$scrollTo('.is-invalid', 100)
                });
            }
        },
        getResumenLiquidacion() {
            this.data.filters = {};
            this.data.eventos = [this.data.evento];

            this.$store.dispatch(this.config.getVuexAlias('back.liquidaciones.eventos.resumen'), this.data).then(res => {
                this.resumen = res.data.data;
            });   
                
        },
        crearLiquidacion() {
            this.creando = true;
            this.data.filters = {};
            this.data.eventos = [this.data.evento];

            this.$store.dispatch(this.config.getVuexAlias('back.liquidaciones.eventos.crear'), this.data).then(res => {
                this.$toast.success(res.data.message);
                this.terminado = true;
                this.creando = false;
            });
        },
        continuar() {
            this.$router.push({ name: 'back.liquidaciones.eventos.index' });
        }
    },
    components: {
        MainContent,
        ContentOrLoading,
        CrearDireccionFiscalModal,
        EditarDireccionFiscalModal,
        ModalEditar,
        RecordatorioComponent,
        RecordatorioTituloComponent
    }
}
</script>